import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTickets, useDeleteTicket, useCancelTicket, canEditTicket, type RecordingTicket } from '../hooks/useTickets';
import { useAuth } from '../hooks/useAuth';
import { isAdminOrSupport, isAdmin, normalizeStatus } from '../utils/roles';
import { userDisplayName, formatSlot } from '../utils/ticketDisplay';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import TicketStatusBadge from '../components/TicketStatusBadge';
import CreateTicketModal from '../components/CreateTicketModal';
import EditTicketModal from '../components/EditTicketModal';
import { Plus, Trash2, Search, User, Calendar, ExternalLink, Ban, Pencil } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'recording', label: 'Recording' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Ready to send' },
  { value: 'sent', label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
];

function TicketRow({
  ticket,
  userRole,
  onEdit,
  onDelete,
  onCancel,
}: {
  ticket: RecordingTicket;
  userRole?: string;
  onEdit: () => void;
  onDelete: () => void;
  onCancel: () => void;
}) {
  const status = normalizeStatus(ticket.status);
  return (
    <div className="etr-surface p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <TicketBody ticket={ticket} />
        <div className="flex flex-wrap gap-2">
          <Link to={`/tickets/${ticket.id}`}>
            <Button variant="outline" size="sm" leftIcon={<ExternalLink className="h-4 w-4" />}>
              View
            </Button>
          </Link>
          {isAdminOrSupport(userRole) && canEditTicket(ticket.status) && (
            <Button variant="outline" size="sm" leftIcon={<Pencil className="h-4 w-4" />} onClick={onEdit}>
              Edit
            </Button>
          )}
          {isAdmin(userRole) && (
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={() => {
                if (window.confirm('Delete this ticket permanently?')) onDelete();
              }}
            >
              Delete
            </Button>
          )}
          {status !== 'cancelled' && status !== 'sent' && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Ban className="h-4 w-4" />}
              onClick={() => {
                if (window.confirm('Cancel this ticket?')) onCancel();
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function TicketBody({ ticket }: { ticket: RecordingTicket }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Link to={`/tickets/${ticket.id}`} className="text-lg font-semibold text-gray-900 hover:text-primary-700">
          {ticket.title}
        </Link>
        {ticket.ticketNumber && (
          <span className="px-2 py-0.5 text-xs font-mono rounded border bg-gray-50 text-gray-600">{ticket.ticketNumber}</span>
        )}
        <TicketStatusBadge status={ticket.status} />
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-2">
        {ticket.slotStartAt && (
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {formatSlot(ticket.slotStartAt)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <User className="h-4 w-4" />
          {ticket.assignedTo ? `Assigned: ${userDisplayName(ticket.assignedTo)}` : 'Unassigned'}
        </span>
        {ticket.requestedBy && <span>Requested by {userDisplayName(ticket.requestedBy)}</span>}
      </div>
      {ticket.notes && <p className="text-sm text-gray-600 bg-gray-50 rounded p-2">{ticket.notes}</p>}
    </div>
  );
}

const Tickets: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status');
  const validInitial =
    initialStatus && STATUS_OPTIONS.some((o) => o.value === initialStatus) ? initialStatus : 'all';
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(validInitial);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTicket, setEditTicket] = useState<RecordingTicket | null>(null);

  const canAccess = user && isAdminOrSupport(user.role);
  const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined;

  const { data: ticketsData, isLoading } = useTickets(filters);
  const deleteTicketMutation = useDeleteTicket();
  const cancelTicketMutation = useCancelTicket();
  const tickets = ticketsData?.tickets || [];

  const filteredTickets = tickets.filter((ticket) => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (
      ticket.title.toLowerCase().includes(q) ||
      (ticket.ticketNumber?.toLowerCase().includes(q) ?? false) ||
      userDisplayName(ticket.assignedTo).toLowerCase().includes(q) ||
      userDisplayName(ticket.requestedBy).toLowerCase().includes(q) ||
      (ticket.customerName?.toLowerCase().includes(q) ?? false) ||
      (ticket.customerPhone?.toLowerCase().includes(q) ?? false)
    );
  });

  if (!canAccess) {
    return (
      <div className="etr-page">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Admin or Customer Support access is required.
        </div>
      </div>
    );
  }

  return (
    <div className="etr-page">
      <div className="etr-page-header">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="etr-page-title mb-2">Recording tickets</h1>
            <p className="etr-page-subtitle">Create and manage video recording requests.</p>
          </div>
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreateModal(true)}>
            New ticket
          </Button>
        </div>
      </div>

      <div className="etr-surface p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets�"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="etr-input pl-10 w-full"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="etr-input px-4 py-2">
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <Empty>No tickets found.</Empty>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              userRole={user?.role}
              onEdit={() => setEditTicket(ticket)}
              onDelete={() => deleteTicketMutation.mutate(ticket.id)}
              onCancel={() => cancelTicketMutation.mutate(ticket.id)}
            />
          ))}
        </div>
      )}

      <CreateTicketModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />
      <EditTicketModal open={!!editTicket} ticket={editTicket} onClose={() => setEditTicket(null)} />
    </div>
  );
};

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="etr-surface p-12 text-center text-gray-500">{children}</div>;
}

export default Tickets;
